import axios from 'axios';
import { mockDb } from './mockData';

// Create a configured axios instance
const getBaseUrl = () => {
    return '/api';
};

const api = axios.create({
    baseURL: getBaseUrl(),
    headers: {
        'Content-Type': 'application/json',
    },
});

// Mocking direct api.get calls for reports not in separate services
const originalGet = api.get;
api.get = (url, config) => {
    if (url === 'reports/pnl') return mockDb.get('projects'); // Or specific pnl mock
    if (url === 'reports/sales') return Promise.resolve({
        data: {
            pendingInvoices: [],
            unbilledDeliveries: [],
            customerSales: [],
            salesTaxInvoices: []
        }
    });
    return originalGet.call(api, url, config);
};

// Add a request interceptor to attach the token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('rsms_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Add a response interceptor to handle errors globally
api.interceptors.response.use(
    (response) => response,
    (error) => {
        console.error('API Error:', error);
        return Promise.reject(error);
    }
);

export const tenderService = {
    getAll: () => mockDb.get('tenders'),
    getById: (id) => mockDb.getById('tenders', id),
    getStats: () => Promise.resolve({ data: { total: 3, pending: 1, awarded: 1, closed: 1 } }),
    create: (data) => mockDb.create('tenders', data),
    update: (id, data) => mockDb.update('tenders', id, data),
    delete: (id) => mockDb.delete('tenders', id),
    updateStage: (id, data) => mockDb.update('tenders', id, data),
    uploadFiles: (formData) => Promise.resolve({ data: { url: 'mock_url' } }),
};

export const quotationService = {
    getAll: () => mockDb.get('quotations'),
    getById: (id) => mockDb.getById('quotations', id),
    create: (data) => mockDb.create('quotations', data),
    update: (id, data) => mockDb.update('quotations', id, data),
    delete: (id) => mockDb.delete('quotations', id),
    updateStage: (id, data) => mockDb.update('quotations', id, data),
};

export const productService = {
    getAll: () => mockDb.get('products'),
    create: (data) => mockDb.create('products', data),
    update: (id, data) => mockDb.update('products', id, data),
    delete: (id) => mockDb.delete('products', id),

    getShipments: () => mockDb.get('shipments'),
    createShipment: (data) => mockDb.create('shipments', data),
    updateShipment: (id, data) => mockDb.update('shipments', id, data),
    deleteShipment: (id) => mockDb.delete('shipments', id),

    getTransfers: () => mockDb.get('transfers'),
    createTransfer: (data) => mockDb.create('transfers', data),
    updateTransfer: (id, data) => mockDb.update('transfers', id, data),
    deleteTransfer: (id) => mockDb.delete('transfers', id),

    getStockOuts: () => mockDb.get('stockout'),
    createStockOut: (data) => mockDb.create('stockout', data),
    updateStockOut: (id, data) => mockDb.update('stockout', id, data),
    deleteStockOut: (id) => mockDb.delete('stockout', id),
};

export const referenceService = {
    getOrgs: () => mockDb.get('organizations'),
    createOrg: (data) => mockDb.create('organizations', data),
    updateOrg: (id, data) => mockDb.update('organizations', id, data),
    deleteOrg: (id) => mockDb.delete('organizations', id),

    getProjects: () => mockDb.get('projects'),
    createProject: (data) => mockDb.create('projects', data),
    updateProject: (id, data) => mockDb.update('projects', id, data),
    deleteProject: (id) => mockDb.delete('projects', id),

    uploadProjectDoc: (id, formData) => Promise.resolve({ data: { success: true } }),
    deleteProjectDoc: (docId) => Promise.resolve({ data: { success: true } }),
    updateProjectDoc: (docId, data) => Promise.resolve({ data: { success: true } }),

    getContacts: () => mockDb.get('contacts'),
    createContact: (data) => mockDb.create('contacts', data),
    updateContact: (id, data) => mockDb.update('contacts', id, data),
    deleteContact: (id) => mockDb.delete('contacts', id),
};

export const authService = {
    login: (credentials) => {
        const user = { id: 1, name: 'Bilal Ahmed', role: 'Administrator', email: credentials.email || 'admin@rsms.com' };
        localStorage.setItem('rsms_token', 'mock_token');
        localStorage.setItem('rsms_user', JSON.stringify(user));
        return Promise.resolve({ data: { token: 'mock_token', user } });
    },
    register: (userData) => mockDb.create('users', userData),
    getAllUsers: () => mockDb.get('users'),
    getUser: (id) => mockDb.getById('users', id),
    updateUser: (id, data) => mockDb.update('users', id, data),
    deleteUser: (id) => mockDb.delete('users', id),
};

export const reportService = {
    getStats: () => Promise.resolve({
        data: {
            counts: {
                totalRecords: 24,
                activeProjects: 12,
                pendingTenders: 5,
                activeQuotations: 8,
                stockItems: 156,
                organizations: 45
            },
            chartData: {
                labels: ['Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'],
                records: [10, 15, 8, 22, 18, 24],
                projects: [2, 5, 3, 8, 4, 12]
            },
            tenderStats: {
                labels: ['Technical Bid', 'Financial Bid', 'Awarded', 'Contract Signed', 'Completed', 'Rejected'],
                data: [5, 3, 2, 1, 1, 0]
            },
            quotationStats: {
                labels: ['Draft', 'Sent', 'Accepted', 'Awarded', 'Rejected', 'Expired'],
                data: [4, 8, 3, 2, 1, 0]
            },
            projectStats: {
                labels: ['Planning', 'Active', 'On Hold', 'Completed'],
                data: [5, 12, 2, 3]
            },
            stockMovement: {
                labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
                inflow: [45, 120, 85, 0, 0, 0],
                outflow: [12, 30, 45, 0, 0, 0]
            },
            earnestMoneyStats: { total: 2360000, released: 1610000, pending: 750000 },
            performanceSecurityStats: { total: 3270000, released: 420000, pending: 2850000 },
            tenders: [
                { id: 1, title: 'Medical Equipment Supply', earnestMoneyAmount: 750000, earnestMoneyReleased: false, currentStage: 3 },
                { id: 2, title: 'Enterprise ERP Implementation', earnestMoneyAmount: 1400000, earnestMoneyReleased: true, currentStage: 9 },
                { id: 3, title: 'Furniture Supply', earnestMoneyAmount: 210000, earnestMoneyReleased: true, currentStage: 14 }
            ],
            revenue: 1062000,
            profit: 162000,
            activeTenders: 5
        }
    }),
    exportSales: () => Promise.resolve({ data: new Blob() }),
    exportSalesPDF: () => Promise.resolve({ data: new Blob() }),
    exportTendersCSV: () => Promise.resolve({ data: new Blob() }),
    exportTendersPDF: () => Promise.resolve({ data: new Blob() }),
    exportQuotationsCSV: () => Promise.resolve({ data: new Blob() }),
    exportQuotationsPDF: () => Promise.resolve({ data: new Blob() }),
    exportPnLCSV: () => Promise.resolve({ data: new Blob() }),
    exportPnLPDF: () => Promise.resolve({ data: new Blob() }),
    exportStockCSV: () => Promise.resolve({ data: new Blob() }),
    exportStockPDF: () => Promise.resolve({ data: new Blob() }),
    exportAllZip: () => Promise.resolve({ data: new Blob() }),
};

export const companyService = {
    getProfile: () => mockDb.get('company'),
    getBranding: () => mockDb.get('company'),
    updateProfile: (data) => mockDb.update('company', null, data),
};

export const recordService = {
    getAll: () => mockDb.get('records'),
    create: (data) => mockDb.create('records', data),
    update: (id, data) => mockDb.update('records', id, data),
    delete: (id) => mockDb.delete('records', id),
    uploadFiles: (formData) => Promise.resolve({ data: { url: 'mock_record_url' } }),
};

export const logService = {
    getAll: (params) => mockDb.get('logs'),
    create: (data) => mockDb.create('logs', data),
};

export const notificationService = {
    getAll: () => mockDb.get('notifications'),
    create: (data) => mockDb.create('notifications', data),
    markRead: (id) => mockDb.update('notifications', id, { read: true }),
    markAllRead: () => {
        // Logic to mark all as read would go here
        return Promise.resolve({ data: { success: true } });
    },
    clearAll: () => {
        // Logic to clear all would go here
        return Promise.resolve({ data: { success: true } });
    }
};

export const warehouseService = {
    getAll: () => mockDb.get('warehouses'),
    create: (data) => mockDb.create('warehouses', data),
    update: (id, data) => mockDb.update('warehouses', id, data),
    delete: (id) => mockDb.delete('warehouses', id),
};

export const brandService = {
    getAll: () => mockDb.get('brands'),
    create: (data) => mockDb.create('brands', data),
    update: (id, data) => mockDb.update('brands', id, data),
    delete: (id) => mockDb.delete('brands', id),
};

export default api;
